import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { ConfiguracionCamion, RestriccionCirculacion } from '@manejo-alcanza/contracts';
import {
  cambiarEstadoVehiculo,
  cerrarRestriccion,
  configuracionCamion,
  crearRestriccion,
  crearVehiculo,
  fecha,
  marcarDiaSinCamion,
  quitarDiaSinCamion,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla } from '../componentes/basicos';

/**
 * F2 — cuándo puede salir el camión.
 *
 * El calendario del CEDI dice cuándo puede **recibir** el cliente. Esta pantalla
 * es la otra mitad: cuándo podemos **salir** nosotros.
 *
 * **Nada de esto vive en el código.** El pico y placa cambia cada seis meses, y
 * una constante en el código obligaría a un despliegue dos veces al año para un
 * dato que el administrador conoce mejor que el programador. Mientras no esté
 * digitado, el sistema dice «no sé» y no «se puede salir».
 */
export function Camion(): ReactNode {
  const consulta = useQuery({ queryKey: ['camion'], queryFn: configuracionCamion });

  if (consulta.isLoading) return <Cargando que="la configuración del camión" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const c = consulta.data!;
  const vigente = c.restricciones.find((r) => r.vigenteHoy) ?? null;
  const activos = c.vehiculos.filter((v) => v.activo);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">El camión</h1>
        <p className="mt-1 text-tenue">
          Cuándo podemos salir. El motor de fechas descuenta de los días que propone los
          que el camión no puede hacer.
        </p>
      </header>

      {activos.length === 0 && (
        <Aviso>
          No hay ningún camión registrado, así que el sistema no puede decir si puede
          circular. Mientras tanto responde «no sé», que es lo honesto.
        </Aviso>
      )}
      {activos.length > 0 && vigente === null && (
        <Aviso>
          No hay ningún pico y placa vigente hoy. Cambia cada seis meses: hasta que se
          digite, el sistema no sabe si el camión puede salir.
        </Aviso>
      )}

      <Vehiculos configuracion={c} />
      <PicoYPlaca configuracion={c} />
      <DiasSinCamion configuracion={c} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function Vehiculos({ configuracion: c }: { configuracion: ConfiguracionCamion }): ReactNode {
  const cliente = useQueryClient();
  const [placa, setPlaca] = useState('');
  const [anio, setAnio] = useState('');
  const [descripcion, setDescripcion] = useState('');

  const refrescar = () => cliente.invalidateQueries({ queryKey: ['camion'] });

  const crear = useMutation({
    mutationFn: () =>
      crearVehiculo({
        placa: placa.trim().toUpperCase(),
        anioModelo: Number(anio),
        ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
      }),
    onSuccess: () => {
      setPlaca('');
      setAnio('');
      setDescripcion('');
      void refrescar();
    },
  });

  const estado = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      cambiarEstadoVehiculo(id, activo),
    onSuccess: () => void refrescar(),
  });

  const listo = /^[A-Za-z0-9]{5,8}$/.test(placa.trim()) && /^\d{4}$/.test(anio);

  return (
    <Card titulo="Los camiones">
      {c.vehiculos.length > 0 && (
        <Tabla>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-tenue">
              <th className="py-1.5">Placa</th>
              <th>Año modelo</th>
              <th>Descripción</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {c.vehiculos.map((v) => (
              <tr key={v.id} className="border-t border-borde">
                <td className="py-1.5 font-mono">{v.placa}</td>
                <td className="cifra">{v.anioModelo}</td>
                <td className="text-tenue">
                  {v.descripcion ?? '—'}
                  {!v.activo && <span className="ml-2 text-xs">(desactivado)</span>}
                </td>
                <td className="text-right">
                  {/* Un vehículo con histórico no se elimina: se desactiva. */}
                  <button
                    type="button"
                    className="text-sm underline underline-offset-2 text-tenue"
                    onClick={() => estado.mutate({ id: v.id, activo: !v.activo })}
                  >
                    {v.activo ? 'Desactivar' : 'Reactivar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      {crear.isError && <Error_>{(crear.error as Error).message}</Error_>}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-tenue">Placa</span>
          <input
            value={placa}
            onChange={(e) => setPlaca(e.target.value.toUpperCase())}
            placeholder="ABC123"
            className="w-28 rounded-md border border-borde px-2 py-1.5 font-mono text-sm uppercase"
          />
        </label>
        <label className="text-sm">
          {/* El año del modelo, no el de matrícula: de ahí sale la antigüedad. */}
          <span className="mb-1 block text-tenue">Año modelo</span>
          <input
            inputMode="numeric"
            value={anio}
            onChange={(e) => setAnio(e.target.value)}
            placeholder="2004"
            className="cifra w-24 rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex-1 text-sm">
          <span className="mb-1 block text-tenue">Descripción (opcional)</span>
          <input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </label>
        <Boton onClick={() => crear.mutate()} deshabilitado={!listo || crear.isPending}>
          Registrar
        </Boton>
      </div>
      <p className="mt-2 text-xs text-tenue">
        La placa va sin espacios ni guiones, como la tarjeta de propiedad. El pico y placa
        se lee de su último dígito.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------

const DIAS = [
  ['digitosLun', 'Lun'],
  ['digitosMar', 'Mar'],
  ['digitosMie', 'Mié'],
  ['digitosJue', 'Jue'],
  ['digitosVie', 'Vie'],
  ['digitosSab', 'Sáb'],
  ['digitosDom', 'Dom'],
] as const;

function PicoYPlaca({ configuracion: c }: { configuracion: ConfiguracionCamion }): ReactNode {
  const cliente = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [desde, setDesde] = useState('');
  const [antiguedad, setAntiguedad] = useState('');
  const [digitos, setDigitos] = useState<Record<string, string>>({});

  const refrescar = () => cliente.invalidateQueries({ queryKey: ['camion'] });

  const crear = useMutation({
    mutationFn: () =>
      crearRestriccion({
        nombre: nombre.trim(),
        vigenteDesde: desde,
        digitosLun: digitos['digitosLun'] ?? '',
        digitosMar: digitos['digitosMar'] ?? '',
        digitosMie: digitos['digitosMie'] ?? '',
        digitosJue: digitos['digitosJue'] ?? '',
        digitosVie: digitos['digitosVie'] ?? '',
        digitosSab: digitos['digitosSab'] ?? '',
        digitosDom: digitos['digitosDom'] ?? '',
        antiguedadMinimaAnios: antiguedad.trim() === '' ? null : Number(antiguedad),
      }),
    onSuccess: () => {
      setAbierto(false);
      setNombre('');
      setDesde('');
      setAntiguedad('');
      setDigitos({});
      void refrescar();
    },
  });

  const cerrar = useMutation({
    mutationFn: ({ id, hasta }: { id: string; hasta: string }) =>
      cerrarRestriccion(id, { vigenteHasta: hasta }),
    onSuccess: () => void refrescar(),
  });

  const bienEscritos = DIAS.every(([clave]) => /^([0-9](,[0-9])*)?$/.test(digitos[clave] ?? ''));
  const listo = nombre.trim().length >= 3 && desde !== '' && bienEscritos;

  return (
    <Card titulo="El pico y placa">
      <p className="text-sm text-tenue">
        Cambia cada seis meses. <strong>Uno nuevo cierra el anterior</strong>: no se edita
        ninguno, para que después se pueda saber qué regía cada día.
      </p>

      {c.restricciones.length > 0 && (
        <div className="mt-3">
          <Tabla>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-tenue">
                <th className="py-1.5">Norma</th>
                <th>Vigencia</th>
                {DIAS.map(([clave, etiqueta]) => (
                  <th key={clave} className="text-center">
                    {etiqueta}
                  </th>
                ))}
                <th>Aplica desde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {c.restricciones.map((r) => (
                <FilaRestriccion
                  key={r.id}
                  restriccion={r}
                  alCerrar={(hasta) => cerrar.mutate({ id: r.id, hasta })}
                />
              ))}
            </tbody>
          </Tabla>
        </div>
      )}

      {crear.isError && <Error_>{(crear.error as Error).message}</Error_>}

      <div className="mt-4">
        <Boton onClick={() => setAbierto((v) => !v)}>
          {abierto ? 'Cancelar' : 'Digitar el pico y placa'}
        </Boton>
      </div>

      {abierto && (
        <div className="mt-3 space-y-3 rounded-md border border-borde p-3">
          <div className="flex flex-wrap gap-3">
            <label className="flex-1 text-sm">
              <span className="mb-1 block text-tenue">Cómo se llama la norma</span>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Pico y placa Bogotá 2026-I"
                className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-tenue">Vigente desde</span>
              <input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className="rounded-md border border-borde px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          <div>
            <span className="mb-1 block text-sm text-tenue">
              Los últimos dígitos que NO circulan cada día, como <code>1,2</code>
            </span>
            <div className="flex flex-wrap gap-2">
              {DIAS.map(([clave, etiqueta]) => (
                <label key={clave} className="text-xs">
                  <span className="mb-1 block text-tenue">{etiqueta}</span>
                  <input
                    value={digitos[clave] ?? ''}
                    onChange={(e) => setDigitos((d) => ({ ...d, [clave]: e.target.value }))}
                    placeholder="—"
                    className="w-16 rounded-md border border-borde px-2 py-1.5 text-center text-sm"
                  />
                </label>
              ))}
            </div>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block text-tenue">
              Solo aplica a camiones con al menos estos años (opcional)
            </span>
            <input
              inputMode="numeric"
              value={antiguedad}
              onChange={(e) => setAntiguedad(e.target.value)}
              placeholder="vacío = aplica a todos"
              className="w-56 rounded-md border border-borde px-2 py-1.5 text-sm"
            />
            {/*
              No hay un número puesto por defecto a propósito: el «menos de 20
              años» de la concepción era una conclusión, no un dato verificado.
            */}
            <span className="mt-1 block text-xs text-tenue">
              Se compara contra el año modelo del camión. Si lo dejas vacío, la norma
              aplica a todos.
            </span>
          </label>

          <Boton onClick={() => crear.mutate()} deshabilitado={!listo || crear.isPending}>
            Guardar y cerrar el anterior
          </Boton>
        </div>
      )}
    </Card>
  );
}

function FilaRestriccion({
  restriccion: r,
  alCerrar,
}: {
  restriccion: RestriccionCirculacion;
  alCerrar: (hasta: string) => void;
}): ReactNode {
  const [hasta, setHasta] = useState('');

  return (
    <tr className={`border-t border-borde ${r.vigenteHoy ? '' : 'text-tenue'}`}>
      <td className="py-1.5">
        {r.nombre}
        {r.vigenteHoy && (
          <span className="ml-2 rounded-full bg-acento-tenue px-2 py-0.5 text-[0.7rem] font-semibold text-acento">
            VIGENTE
          </span>
        )}
      </td>
      <td className="whitespace-nowrap">
        {fecha(r.vigenteDesde)} → {r.vigenteHasta ? fecha(r.vigenteHasta) : 'abierta'}
      </td>
      {DIAS.map(([clave]) => (
        <td key={clave} className="text-center font-mono text-xs">
          {r[clave] === '' ? '—' : r[clave]}
        </td>
      ))}
      <td className="cifra">
        {r.antiguedadMinimaAnios === null ? 'todos' : `${r.antiguedadMinimaAnios} años`}
      </td>
      <td className="text-right">
        {r.vigenteHasta === null && (
          <span className="flex items-center justify-end gap-1">
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="rounded-md border border-borde px-1.5 py-1 text-xs"
            />
            <button
              type="button"
              disabled={hasta === ''}
              className="text-xs underline underline-offset-2 disabled:opacity-40"
              onClick={() => alCerrar(hasta)}
            >
              Cerrar
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

function DiasSinCamion({ configuracion: c }: { configuracion: ConfiguracionCamion }): ReactNode {
  const cliente = useQueryClient();
  const [dia, setDia] = useState('');
  const [motivo, setMotivo] = useState('');
  const [vehiculoId, setVehiculoId] = useState('');

  const refrescar = () => cliente.invalidateQueries({ queryKey: ['camion'] });

  const marcar = useMutation({
    mutationFn: () =>
      marcarDiaSinCamion({
        fecha: dia,
        motivo: motivo.trim(),
        vehiculoId: vehiculoId === '' ? null : vehiculoId,
      }),
    onSuccess: () => {
      setDia('');
      setMotivo('');
      void refrescar();
    },
  });

  const quitar = useMutation({
    mutationFn: (id: string) => quitarDiaSinCamion(id),
    onSuccess: () => void refrescar(),
  });

  const listo = dia !== '' && motivo.trim().length >= 3;

  return (
    <Card titulo="Los días sin camión">
      <div className="space-y-1.5 text-sm text-tenue">
        <p>
          <strong className="text-texto">
            Días concretos en que no se va a salir a repartir.
          </strong>{' '}
          No es una regla que se repita como el pico y placa: es un día suelto del
          calendario, con su motivo. Mantenimiento o revisión tecnomecánica, el camión
          prestado, o un festivo en que no se reparte.
        </p>
        <p>
          <strong className="text-texto">Para qué sirve:</strong> cuando el sistema contesta
          «qué día cabe» para una orden,{' '}
          <strong className="text-texto">no propone ninguno de estos días</strong>, aunque
          el CEDI reciba ese día. Y si una orden está pedida justo para uno, lo avisa en el
          expediente y la saca en la lista de trabajo del CAU.
        </p>
      </div>

      {c.diasSinCamion.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {c.diasSinCamion.map((d) => (
            <li key={d.id} className="flex flex-wrap items-baseline gap-2 border-t border-borde pt-1">
              <span className="w-28 font-medium">{fecha(d.fecha)}</span>
              <span>{d.motivo}</span>
              <span className="text-xs text-tenue">
                {d.placa ?? 'ningún camión sale'}
                {d.creadoPor && ` · lo marcó ${d.creadoPor}`}
              </span>
              <button
                type="button"
                className="ml-auto text-xs underline underline-offset-2 text-tenue"
                onClick={() => quitar.mutate(d.id)}
              >
                Quitar
              </button>
            </li>
          ))}
        </ul>
      )}

      {marcar.isError && <Error_>{(marcar.error as Error).message}</Error_>}

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-tenue">Día</span>
          <input
            type="date"
            value={dia}
            onChange={(e) => setDia(e.target.value)}
            className="rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex-1 text-sm">
          <span className="mb-1 block text-tenue">Motivo</span>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="mantenimiento programado"
            className="w-full rounded-md border border-borde px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-tenue">Camión</span>
          <select
            value={vehiculoId}
            onChange={(e) => setVehiculoId(e.target.value)}
            className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
          >
            <option value="">Ninguno sale</option>
            {c.vehiculos
              .filter((v) => v.activo)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.placa}
                </option>
              ))}
          </select>
        </label>
        <Boton onClick={() => marcar.mutate()} deshabilitado={!listo || marcar.isPending}>
          Marcar
        </Boton>
      </div>
      <p className="mt-2 text-xs text-tenue">
        El motivo es obligatorio: dentro de dos meses, «2026-04-02» a secas no explica por
        qué esa semana no se entregó. <strong>Ninguno sale</strong> bloquea todos los
        camiones, que es lo que se quiere para un festivo.
      </p>
    </Card>
  );
}
